import { Link } from 'react-router-dom';
import { Heart, ShoppingBag, Star } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductCard = ({ product, onWishlistChange }) => {
  const { token, isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [loading, setLoading] = useState(false);

  const isOutOfStock = product.stock <= 0;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price) + ' MXN';
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isOutOfStock) {
      toast.error('Producto agotado');
      return;
    }

    if (!isAuthenticated) {
      toast.error('Inicia sesión para agregar al carrito');
      return;
    }

    try {
      setLoading(true);
      await addToCart(product.id, 1);
      toast.success('Agregado al carrito');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al agregar al carrito';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      toast.error('Inicia sesión para guardar favoritos');
      return;
    }

    try {
      if (isInWishlist) {
        await axios.delete(`${API}/wishlist/remove/${product.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsInWishlist(false);
        toast.success('Eliminado de favoritos');
      } else {
        await axios.post(
          `${API}/wishlist/add`,
          { product_id: product.id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setIsInWishlist(true);
        toast.success('Agregado a favoritos');
      }
      if (onWishlistChange) onWishlistChange();
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    }
  };

  return (
    <Link 
      to={`/product/${product.id}`} 
      className="product-card group block"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container */}
      <div className={`product-image-container bg-[#F5F0EB] aspect-[3/4] mb-4 relative ${isOutOfStock ? 'opacity-70' : ''}`}>
        <img
          src={product.image_url}
          alt={product.name}
          className="product-image w-full h-full object-cover"
          loading="lazy"
        />
        
        {/* Hover Actions - Only show if in stock */}
        {!isOutOfStock && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-end justify-center pb-6 opacity-0 group-hover:opacity-100">
            <button
              onClick={handleAddToCart}
              disabled={loading}
              className="bg-white text-[#1C1917] px-6 py-3 rounded-full text-sm tracking-wide hover:bg-[#BA7B7C] hover:text-white transition-all transform translate-y-4 group-hover:translate-y-0 flex items-center space-x-2"
              data-testid={`add-to-cart-${product.id}`}
            >
              <ShoppingBag className="w-4 h-4" strokeWidth={1.5} />
              <span>Agregar</span>
            </button>
          </div>
        )}

        {/* Out of Stock Overlay */}
        {isOutOfStock && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="bg-[#1C1917]/80 text-white px-4 py-2 rounded-full text-sm tracking-wide">
              Agotado
            </span>
          </div>
        )}

        {/* Wishlist Button */}
        <button
          onClick={handleWishlistToggle}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-all shadow-soft"
          data-testid={`wishlist-btn-${product.id}`}
        >
          <Heart 
            className={`w-5 h-5 heart-icon ${isInWishlist ? 'filled text-[#BA7B7C]' : 'text-[#57534E]'}`} 
            strokeWidth={1.5}
            fill={isInWishlist ? '#BA7B7C' : 'none'}
          />
        </button>

        {/* Featured Badge */}
        {product.featured && !isOutOfStock && (
          <div className="absolute top-4 left-4 bg-[#BA7B7C] text-white px-3 py-1 rounded-full text-xs tracking-wide">
            Destacado
          </div>
        )}

        {/* Low Stock Badge */}
        {product.stock > 0 && product.stock <= 3 && (
          <div className="absolute top-4 left-4 bg-amber-500 text-white px-3 py-1 rounded-full text-xs tracking-wide">
            ¡Últimas {product.stock}!
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="space-y-2">
        <p className="text-xs tracking-[0.15em] uppercase text-[#A8A29E]">
          {product.category}
        </p>
        <h3 
          className={`text-lg font-light transition-colors ${isOutOfStock ? 'text-[#A8A29E]' : 'text-[#1C1917] group-hover:text-[#BA7B7C]'}`}
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {product.name}
        </h3>
        
        {/* Rating */}
        {product.review_count > 0 && (
          <div className="flex items-center space-x-1">
            {[...Array(5)].map((_, i) => (
              <Star
                key={`star-${product.id}-${i}`}
                className={`w-3 h-3 ${i < Math.round(product.average_rating) ? 'text-[#BA7B7C] fill-[#BA7B7C]' : 'text-[#E7E5E4]'}`}
              />
            ))}
            <span className="text-xs text-[#A8A29E] ml-1">({product.review_count})</span>
          </div>
        )}
        
        <p className={`text-base font-medium ${isOutOfStock ? 'text-[#A8A29E]' : 'text-[#1C1917]'}`}>
          {formatPrice(product.price)}
        </p>
      </div>
    </Link>
  );
};

export default ProductCard;
